import { FC, useCallback, useMemo } from 'react';
import { InterviewStatus, ProfileForAdmin, User } from '../../../common/Types';
import {
  getApplicationStateIdentifier,
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../../config';
import styled from 'styled-components';
import { downloadInterviewTranscript } from './ProfileDetails/InterviewTranscriptUtils';
import { InterviewStatusLabel } from './InterviewStatusLabel';

const PASSING_RATING = 75;

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const DetailList = styled.dl`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 1em;
`;

export type ProfileDetailsProps = {
  authorization: string;
  user: User;
  profileId: string;
};

export const ProfileDetails: FC<ProfileDetailsProps> = ({ authorization, user, profileId }) => {
  const PROFILE_FOR_ADMIN_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/user-profile',
      args: [profileId],
    }),
    [authorization, profileId]
  );
  const INTERVIEW_TRANSCRIPT_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: '/interview-transcript',
      args: [profileId],
    }),
    [authorization, profileId]
  );
  const userProfileAppStateId = useMemo(() => getApplicationStateIdentifier(), [profileId]);
  const interviewTranscriptAppStateId = useMemo(() => getApplicationStateIdentifier(), [profileId]);
  const { value: profileValue } = useApplicationStateValue(userProfileAppStateId);
  const { value: interviewTranscriptValue = [] } = useApplicationStateValue(interviewTranscriptAppStateId);
  const {
    status = InterviewStatus.INTERVIEW_NOT_STARTED,
    rating = 0,
    linkedInUrl = '',
    city = '',
    state = '',
    audioSampleDownloadUrl = '',
    resumeDownloadUrl = '',
  }: Partial<ProfileForAdmin> = profileValue || {};
  const { loading: profileLoading = false } = useApplicationStateLoader({
    identifier: userProfileAppStateId,
    remoteProcedureCall: PROFILE_FOR_ADMIN_RPC,
  });
  const { loading: interviewTranscriptLoading = false } = useApplicationStateLoader({
    identifier: interviewTranscriptAppStateId,
    remoteProcedureCall: INTERVIEW_TRANSCRIPT_RPC,
  });
  const isLoading = profileLoading || interviewTranscriptLoading;
  const onOpenInterviewTranscript = useCallback(() => {
    downloadInterviewTranscript(user, profileValue, interviewTranscriptValue);
  }, [user, profileValue, interviewTranscriptValue]);

  return (
    <div>
      {isLoading ? (
        <article aria-busy="true"></article>
      ) : (
        <DetailList>
          <dt>Interview Status</dt>
          <dd>
            <InterviewStatusLabel status={status} />
          </dd>
          {status === InterviewStatus.INTERVIEW_COMPLETED ? (
            <>
              <dt>Interview Rating</dt>
              <dd>
                <span
                  style={{
                    backgroundColor:
                      rating >= PASSING_RATING ? 'green' : rating >= 60 ? 'yellow' : rating >= 40 ? 'orange' : 'red',
                    color:
                      rating >= PASSING_RATING ? 'white' : rating >= 60 ? 'black' : rating >= 40 ? 'white' : 'white',
                    padding: '0.25em',
                    borderRadius: '0.25em',
                  }}
                >
                  {rating}% &nbsp;
                  {rating >= PASSING_RATING ? 'Potential Fast Start' : 'Average Start Up'}
                </span>
              </dd>
            </>
          ) : undefined}
          {city ? (
            <>
              <dt>City</dt>
              <dd>{city}</dd>
            </>
          ) : undefined}
          {state ? (
            <>
              <dt>State</dt>
              <dd>{state}</dd>
            </>
          ) : undefined}
          {linkedInUrl ? (
            <>
              <dt>LinkedIn URL</dt>
              <dd>
                <a href={linkedInUrl} target="_blank" rel="noreferrer">
                  {linkedInUrl}
                </a>
              </dd>
            </>
          ) : undefined}
          {resumeDownloadUrl ? (
            <>
              <dt>Resume</dt>
              <dd>
                <a href={resumeDownloadUrl} target="_blank" download>
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
          {audioSampleDownloadUrl ? (
            <>
              <dt>Audio Sample</dt>
              <dd>
                <a href={audioSampleDownloadUrl} target="_blank" download>
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
          {profileValue && interviewTranscriptValue && interviewTranscriptValue.length > 0 ? (
            <>
              <dt>Interview Transcript</dt>
              <dd>
                <a
                  onClick={onOpenInterviewTranscript}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  Download
                </a>
              </dd>
            </>
          ) : undefined}
        </DetailList>
      )}
    </div>
  );
};
